import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  TextField,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Divider,
  Paper
} from '@mui/material';

// --- HELPER COMPONENTS ---

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 2 }}>
            {children}
        </Box>
      )}
    </div>
  );
}

const ModalFormRow = ({ label, children }) => (
  <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
    <Grid item xs={12} md={4}>
      <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={12} md={8}>
      {children}
    </Grid>
  </Grid>
);

export default function AddHeirDialog({ open, onClose, matterId, onSave, initialData }) {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [formData, setFormData] = useState(initialData || {});

  useEffect(() => {
    if (open) setFormData(initialData || {});
  }, [open, initialData]);

  const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
      }));
  };

  const handleSave = async () => {
      try {
          await axios.post(`https://demo.insollex.com/api/legal-matter/${matterId}/heirs`, formData);
          if (onSave) onSave();
          onClose();
      } catch (error) {
          console.error("Error saving heir:", error);
          alert("Failed to save heir.");
      }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Add/Edit Heir
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, minHeight: '600px', bgcolor: '#fdfdfd' }}>
        
        {/* MODAL TABS HEADER */}
        <Paper square elevation={0} sx={{ borderBottom: '1px solid #e0e0e0', bgcolor: '#f0f0f0' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                fontSize: '0.75rem',
                minHeight: 32,
                textTransform: 'none',
                fontWeight: 500,
                py: 0.5,
                borderRight: '1px solid #e0e0e0'
              },
              '& .Mui-selected': { bgcolor: '#fff', fontWeight: 'bold' }
            }}
          >
            <Tab label="1. Beneficiary's Details" />
            <Tab label="2. Beneficiary's Details (cont..)" />
            <Tab label="3. Inheritance/Other" />
            <Tab label="4. Beneficiary's Details (cont...)" />
            <Tab label="5. Next-of-Kin Affidavit" />
          </Tabs>
        </Paper>

        {/* ================= TAB 1: BENEFICIARY'S DETAILS ================= */}
        <TabPanel value={tabValue} index={0}>
            <Stack spacing={1}>
                
                <Typography variant="caption" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                    For 4(h) duty deductions select charity. For 4(q) duty deduction select surviving spouse even for qualifying life partners.
                </Typography>

                {/* Heir Type Radio Group */}
                <Grid container alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500 }}>Indicate the type of heir</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <RadioGroup row name="heirType" value={formData.heirType || ''} onChange={handleChange}>
                            <FormControlLabel value="Male" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Male</Typography>} />
                            <FormControlLabel value="Female" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Female</Typography>} />
                            <FormControlLabel value="Juristic" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Juristic</Typography>} />
                            <FormControlLabel value="Charity" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Charity</Typography>} />
                            <FormControlLabel value="Trust" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Trust</Typography>} />
                            <FormControlLabel value="Other" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Other</Typography>} />
                        </RadioGroup>
                    </Grid>
                </Grid>

                {/* Relationship Radio Group */}
                <Grid container alignItems="flex-start" sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500 }}>Heir's relationship to the deceased</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <RadioGroup row name="relationship" value={formData.relationship || ''} onChange={handleChange}>
                            <Grid container>
                                <Grid item xs={4}>
                                    <FormControlLabel value="Surviving Spouse" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Surviving Spouse</Typography>} />
                                    <FormControlLabel value="Mother/Father" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Mother/Father</Typography>} />
                                    <FormControlLabel value="Half-brother/sister" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Half-brother/sister</Typography>} />
                                    <FormControlLabel value="Other" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Other</Typography>} />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControlLabel value="Child" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Child</Typography>} />
                                    <FormControlLabel value="Brother/Sister" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Brother/Sister</Typography>} />
                                    <FormControlLabel value="ChildOfSibling" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Child of brother/sister/half-brother/sister</Typography>} />
                                    <FormControlLabel value="Not related" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Not related</Typography>} />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormControlLabel value="Grandchild" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Grandchild</Typography>} />
                                </Grid>
                            </Grid>
                        </RadioGroup>
                    </Grid>
                </Grid>

                <ModalFormRow label="Describe the relationship for the L&D">
                    <Stack direction="row" spacing={1}>
                        <TextField name="relationshipDescription" value={formData.relationshipDescription || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                        <Button variant="outlined" sx={{ minWidth: '40px', px: 0 }}>...</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Heir's Surname">
                    <TextField name="surname" value={formData.surname || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Heir's First Names">
                    <TextField name="firstNames" value={formData.firstNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Heir's Identity Number">
                    <TextField name="idNumber" value={formData.idNumber || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Heir's Date of Birth">
                    <TextField name="dob" value={formData.dob || ''} onChange={handleChange} sx={{ width: '120px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Heir's Address">
                    <TextField name="address" value={formData.address || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Heir's telephone no.">
                    <TextField name="phone" value={formData.phone || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Heir's fax no.">
                    <TextField name="fax" value={formData.fax || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Heir's cell phone no.">
                    <TextField name="cell" value={formData.cell || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Heir's e-mail address">
                    <TextField name="email" value={formData.email || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Heir's Marital Status">
                    <TextField name="maritalStatus" value={formData.maritalStatus || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Full Names and Surname of Spouse">
                    <TextField name="spouseName" value={formData.spouseName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Heir's Date of Marriage">
                    <TextField name="marriageDate" value={formData.marriageDate || ''} onChange={handleChange} sx={{ width: '120px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Spouse's Identity No">
                    <TextField name="spouseId" value={formData.spouseId || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

            </Stack>
        </TabPanel>

        {/* ================= TAB 2: BENEFICIARY'S DETAILS (CONT..) ================= */}
        <TabPanel value={tabValue} index={1}>
            <Stack spacing={0.5}>
                
                {/* Trust Beneficiaries Text Area */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500 }}>Details of Trust's ultimate beneficiaries</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField 
                            name="trustUltimateBeneficiaries"
                            value={formData.trustUltimateBeneficiaries || ''}
                            onChange={handleChange}
                            fullWidth 
                            multiline 
                            rows={6} 
                            size="small" 
                            variant="outlined" 
                            sx={{ bgcolor: '#fff' }}
                        />
                    </Grid>
                </Grid>

                {/* Trust Legal Checkboxes */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                     <Grid item xs={12} md={8}>
                        <Typography variant="caption">
                            Is this a NON-DISCRETIONARY trust of which property or income is for the sole benefit of the surviving spouse?
                        </Typography>
                     </Grid>
                     <Grid item xs={12} md={1}>
                        <Checkbox name="isNonDiscretionarySpouseBenefit" checked={formData.isNonDiscretionarySpouseBenefit || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                     </Grid>
                     <Grid item xs={12} md={3}>
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(Sect 4q (ii) of Act 45 of 1955 refers)</Typography>
                     </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                     <Grid item xs={12} md={8}>
                        <Typography variant="caption">
                            Is this a NON-DISCRETIONARY trust of which the surviving spouse gets the income generated from Trust capital?
                        </Typography>
                     </Grid>
                     <Grid item xs={12} md={1}>
                        <Checkbox name="isNonDiscretionarySpouseIncome" checked={formData.isNonDiscretionarySpouseIncome || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                     </Grid>
                     <Grid item xs={12} md={3}>
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(Sect 4q (ii) of Act 45 of 1955 refers)</Typography>
                     </Grid>
                </Grid>

                {/* Bank Details Section */}
                <ModalFormRow label="Name of heir's bank">
                    <TextField name="bankName" value={formData.bankName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Name of branch">
                    <TextField name="branchName" value={formData.branchName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Branch code">
                    <TextField name="branchCode" value={formData.branchCode || ''} onChange={handleChange} sx={{ width: '100px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Account type (e.g. current)">
                    <TextField name="accountType" value={formData.accountType || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Account number">
                    <TextField name="accountNumber" value={formData.accountNumber || ''} onChange={handleChange} sx={{ width: '200px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Name of account holder">
                    <TextField name="accountHolder" value={formData.accountHolder || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Heir's Tax Ref. for FICA purposes">
                    <TextField name="taxRefFICA" value={formData.taxRefFICA || ''} onChange={handleChange} sx={{ width: '250px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

            </Stack>
        </TabPanel>

        {/* ================= TAB 3: INHERITANCE/OTHER ================= */}
        <TabPanel value={tabValue} index={2}>
            <Stack spacing={0.5}>

                <ModalFormRow label="Will heir get a cash legacy ito the Will?">
                     <Checkbox name="getsCashLegacy" checked={formData.getsCashLegacy || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Cash legacy amount">
                     <TextField name="cashLegacyAmount" value={formData.cashLegacyAmount || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Made in terms of which clause in the will?">
                    <TextField name="willClause" value={formData.willClause || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Is a usufruct being created over this legacy?">
                     <Checkbox name="usufructCreated" checked={formData.usufructCreated || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="In favour of whom?">
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                         <TextField name="usufructInFavourOf" value={formData.usufructInFavourOf || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                         <Button variant="contained" size="small" sx={{ bgcolor: '#bdbdbd', color: '#000', textTransform: 'none', boxShadow: 'none' }}>Beneficiaries</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Is this usufructuary the surviving spouse?">
                     <Checkbox name="isUsufructSpouse" checked={formData.isUsufructSpouse || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                {/* Residue Fraction Row */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500 }}>Fractional portion of residue due to heir (eg 1/2)</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <TextField name="residueFractionNumerator" value={formData.residueFractionNumerator || '0'} onChange={handleChange} sx={{ width: '60px', '& .MuiInputBase-input': { textAlign: 'center', py: 0.5 } }} size="small" variant="outlined" />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>/</Typography>
                            <TextField name="residueFractionDenominator" value={formData.residueFractionDenominator || '0'} onChange={handleChange} sx={{ width: '60px', '& .MuiInputBase-input': { textAlign: 'center', py: 0.5 } }} size="small" variant="outlined" />
                            <Typography variant="caption" sx={{ ml: 2 }}>(If deceased married icop, residue = 1 and not &frac12;)</Typography>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Residue Percentage Row */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                         <Typography variant="caption" sx={{ fontWeight: 500 }}>Alternatively, heir's percentage of the residue</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <TextField name="residuePercentage" value={formData.residuePercentage || '0.0000000000'} onChange={handleChange} sx={{ width: '140px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                            <Typography variant="caption">(Type % and press Tab to have the percentage converted to a fraction)</Typography>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Minor Section */}
                <ModalFormRow label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>Is this heir a minor?</Typography>}>
                     <Checkbox name="isMinor" checked={formData.isMinor || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="If so, complete full names of guardian/curator">
                    <TextField name="guardianName" value={formData.guardianName || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Address of guardian/curator">
                    <TextField name="guardianAddress" value={formData.guardianAddress || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                {/* Legal Status Checkboxes */}
                <Box sx={{ mt: 2 }}>
                    <ModalFormRow label="Did this heir adiate the benefits under the Will?">
                         <Checkbox name="adiatedBenefits" checked={formData.adiatedBenefits || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Did this heir repudiate benefits under the Will?">
                         <Checkbox name="repudiatedBenefits" checked={formData.repudiatedBenefits || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Is this heir a non-resident?">
                         <Checkbox name="isNonResident" checked={formData.isNonResident || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <Grid item xs={12} md={4}>
                             <Typography variant="caption" sx={{ fontWeight: 500 }}>Did this heir repudiate/renounce his intestate inheritance?</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                             <RadioGroup row name="repudiatedIntestate" value={formData.repudiatedIntestate || ''} onChange={handleChange}>
                                <FormControlLabel value="Yes" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Yes</Typography>} />
                                <FormControlLabel value="No" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">No</Typography>} />
                             </RadioGroup>
                        </Grid>
                    </Grid>

                    <ModalFormRow label="Is this a spouse whom is prohibited from inheriting?">
                         <Checkbox name="prohibitedSpouse" checked={formData.prohibitedSpouse || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>Print this heir on the Next-of-Kin Affidavit?</Typography>}>
                         <Checkbox name="printNextOfKin" checked={formData.printNextOfKin || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>
                </Box>

            </Stack>
        </TabPanel>

        {/* ================= TAB 4: BENEFICIARY'S DETAILS (CONT...) ================= */}
        <TabPanel value={tabValue} index={3}>
            <Stack spacing={0.5}>

                <ModalFormRow label="Who will vote on heir's behalf?">
                    <TextField name="votingProxy" value={formData.votingProxy || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Who does heir nominate as executor?">
                    <TextField name="nominatedExecutor" value={formData.nominatedExecutor || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Date of next-of-kin meeting">
                    <TextField name="nextOfKinMeetingDate" value={formData.nextOfKinMeetingDate || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Names of Heir's Father (if alive)">
                    <TextField name="fatherNameAlive" value={formData.fatherNameAlive || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Names of Heir's Mother (if alive)">
                    <TextField name="motherNameAlive" value={formData.motherNameAlive || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                {/* Inventory Presence Row */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>Heir present when Inventory was taken?</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox name="presentAtInventory" checked={formData.presentAtInventory || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(In the case of an Inventory under section 9 of the Act)</Typography>
                        </Stack>
                    </Grid>
                </Grid>

                <ModalFormRow label="Ultimate beneficiary from a previous estate?">
                     <Checkbox name="ultimateBeneficiaryPrevEstate" checked={formData.ultimateBeneficiaryPrevEstate || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                {/* Fractional Portion Row */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>If so, fractional portion of bare dominium?</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <TextField name="bareDominiumNumerator" value={formData.bareDominiumNumerator || '0'} onChange={handleChange} sx={{ width: '60px', '& .MuiInputBase-input': { textAlign: 'center', py: 0.5 } }} size="small" variant="outlined" />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>/</Typography>
                            <TextField name="bareDominiumDenominator" value={formData.bareDominiumDenominator || '0'} onChange={handleChange} sx={{ width: '60px', '& .MuiInputBase-input': { textAlign: 'center', py: 0.5 } }} size="small" variant="outlined" />
                            <Typography variant="caption" sx={{ ml: 1 }}>(Used for Table A and B calculations for fiduciary/usufructory and like rights)</Typography>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Checked Distribution Row */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>Has this heir's distribution been checked?</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox name="distributionChecked" checked={formData.distributionChecked || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>(If you tick this you will not be able to edit any info on these screens!)</Typography>
                        </Stack>
                    </Grid>
                </Grid>

                <ModalFormRow label="If so, on which date?">
                    <TextField name="distributionCheckedDate" value={formData.distributionCheckedDate || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <Box sx={{ mt: 4, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Only in the event of a Redistribution Agreement, complete the following details:
                    </Typography>

                    <ModalFormRow label="If this heir is to CONTRIBUTE cash ito the Redistribution Agreement - complete the amount">
                        <TextField name="contributeCashAmount" value={formData.contributeCashAmount || '0.00'} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} />
                    </ModalFormRow>

                    <ModalFormRow label="Alternatively, indicate the amount of cash to be RECEIVED by this heir">
                        <TextField name="receiveCashAmount" value={formData.receiveCashAmount || '0.00'} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} />
                    </ModalFormRow>
                </Box>

            </Stack>
        </TabPanel>

        {/* ================= TAB 5: NEXT-OF-KIN AFFIDAVIT ================= */}
        <TabPanel value={tabValue} index={4}>
            <Stack spacing={0.5} sx={{ position: 'relative', minHeight: '500px' }}>

                {/* Section 1 */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
                    If this heir is a child of a predeceased child of the deceased's:
                </Typography>

                <ModalFormRow label="Is this heir an issue of a predeceased child?">
                     <Checkbox name="isIssueOfPredeceasedChild" checked={formData.isIssueOfPredeceasedChild || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Complete the names of predeceased child">
                    <TextField name="predeceasedChildNames" value={formData.predeceasedChildNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Date of death of predeceased child">
                    <TextField name="predeceasedChildDOD" value={formData.predeceasedChildDOD || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                {/* Section 2 */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                    If this heir is a child of a predeceased brother/sister/half-brother/half-sister of the deceased:
                </Typography>

                <ModalFormRow label="Complete names of predeceased sibling">
                    <TextField name="predeceasedSiblingNames" value={formData.predeceasedSiblingNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Date of death of predeceased sibling">
                    <TextField name="predeceasedSiblingDOD" value={formData.predeceasedSiblingDOD || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                {/* Section 3 */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                    If this heir is a half-brother or - sister:
                </Typography>

                <ModalFormRow label="Complete the names of the step-parent">
                    <TextField name="stepParentNames" value={formData.stepParentNames || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                {/* Section 4 */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                    If this heir is deceased:
                </Typography>

                <ModalFormRow label="Date of death of this heir">
                    <TextField name="heirDOD" value={formData.heirDOD || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <Box sx={{ mt: 2 }}>
                    <ModalFormRow label="Print this heir on the Next-of-Kin Affidavit?">
                         <Checkbox name="printNextOfKinTab5" checked={formData.printNextOfKinTab5 || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>
                </Box>

                {/* Totals Button at Bottom Right */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 10 }}>
                    <Button variant="contained" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', minWidth: '100px' }}>
                        Totals
                    </Button>
                </Box>

            </Stack>
        </TabPanel>

      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
         <Button onClick={handleSave} variant="contained" color="success">
            Ok
         </Button>
         <Button onClick={onClose} variant="outlined" color="error">
            Cancel
         </Button>
      </DialogActions>
    </Dialog>
  );
}